import ThreeDot from "@/assets/icons/threeDot.svg";
import GrayFileIcon from "@/assets/icons/GrayFileIcon.svg";
import FileIcon from "@/assets/icons/FileIcon.svg";
import { handleDownload } from "@/utils/handleDownload";
import {
  FileDetails,
  VideoEditingRevisionFeedbackItem,
} from "@/types/projectType";
import Check from "@/assets/icons/GreenCheck.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdrown";
import { formatFileSize } from "@/utils/formatFileSize";
import { Link } from "react-router-dom";

type RevisionFileCardProps = {
  comment: string;
  file: FileDetails;
  active: boolean;
  isApproved?: boolean;
  requiresRevision?: boolean;
  showRequestRevisionForm?: boolean;
  isLoading?: boolean;
  onRequestRevision?: () => void;
  onApprove?: () => void;
  link?: string;
  isLink?: boolean;
  revisionImages?: FileDetails[];
  revisionFeedbackItems?: VideoEditingRevisionFeedbackItem[];
};

export const RevisionFileCard = ({
  file,
  active,
  isApproved,
  showRequestRevisionForm,
  isLoading,
  onRequestRevision,
  comment,
  onApprove,
  link,
  isLink,
  revisionImages = [],
  revisionFeedbackItems = [],
}: RevisionFileCardProps) => {
  const sortedFeedbackItems = [...revisionFeedbackItems].sort((a, b) =>
    `${a.timeStart || ""}-${a.timeEnd || ""}`.localeCompare(
      `${b.timeStart || ""}-${b.timeEnd || ""}`,
    ),
  );
  const hasStructuredFeedback = sortedFeedbackItems.length > 0;

  return (
    <div className="mt-3 mb-4 p-4 border-[1.5px] border-[#F0EEEE] w-full rounded-xl">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 items-center">
          {active ? <FileIcon /> : <GrayFileIcon />}
          <div>
            <h6 className="text-xs text-black font-medium">
              {isLink ? "Drive Link" : file?.name}
            </h6>
            <p className="text-[11px] text-[#959FB2]">
              {isLink ? (
                <Link target="_blank" to={link as string}>
                  View Link
                </Link>
              ) : (
                formatFileSize(file?.size)
              )}
            </p>
          </div>
        </div>
        <div className="hidden relative lg:flex gap-4 items-center">
          {isApproved && (
            <p className="text-xs font-medium flex items-center px-2 py-1 rounded-full gap-x-1 bg-[#E9F5EF] text-[#1D7100]">
              Approved
            </p>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button>
                <ThreeDot />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>
                <p className="text-caption-large font-semibold text-gray-800">
                  Report
                </p>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <p
                  onClick={() =>
                    !isLink &&
                    file?.live_url &&
                    handleDownload(file.live_url as string, file.name)
                  }
                  className="text-caption-large font-semibold text-gray-800"
                >
                  Download
                </p>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {/* {!isApproved && (
        <div className="block lg:hidden gap-3 justify-between items-center mt-5">
          {!showRequestRevisionForm && (
            <button
              onClick={onRequestRevision}
              className="text-[#475467] text-[11px] sm:text-xs font-medium whitespace-nowrap"
            >
              Request revision
            </button>
          )}
          <button
            onClick={onApprove}
            className="flex items-center gap-1 md:gap-2 text-[#1D7100] text-[10px] sm:text-xs tracking-[-0.75] leading-4 py-1 px-2 bg-[#E9F5EF] border border-[#1D7100] rounded-xl"
          >
            <Check />
            <p>{isLoading ? "Approving" : "Approve it"}</p>
          </button>
        </div>
      )} */}
      {comment && (
        <div className="p-3  mt-4 bg-[#F1F1F1] rounded-[10px] border-[#F1F1F1] border">
          <p className="text-[#667085] text-caption-large whitespace-pre-wrap break-words">
            {comment}
          </p>
        </div>
      )}
      {hasStructuredFeedback && (
        <div className="mt-4">
          <p className="text-[13px] mb-3 text-[#475467] font-semibold">
            Revision feedback
          </p>
          <div className="space-y-3">
            {sortedFeedbackItems.map((item, itemIndex) => (
              <div
                key={item.id || itemIndex}
                className="rounded-[10px] border border-[#EAECF0] bg-white p-3"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#EEF4FF] px-2.5 py-1 text-[11px] font-semibold text-[#1A3EA0]">
                    {item.timeStart || item.timeEnd
                      ? `${item.timeStart || "Start"}${
                          item.timeEnd ? ` - ${item.timeEnd}` : ""
                        }`
                      : `Section ${itemIndex + 1}`}
                  </span>
                  <span className="text-[11px] text-[#667085]">
                    {item.images?.length || 0} screenshot
                    {(item.images?.length || 0) === 1 ? "" : "s"}
                  </span>
                </div>
                <p className="text-xs leading-5 text-[#344054] whitespace-pre-wrap break-words">
                  {item.note}
                </p>
                {item.images?.length > 0 && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {item.images.map((image, index) => (
                      <div
                        key={`${item.id}-${image.name}-${index}`}
                        className="border border-[#EAECF0] rounded-[10px] overflow-hidden bg-white"
                      >
                        <div className="aspect-[4/3] bg-[#F7F7F7]">
                          <img
                            src={(image.live_url || image.local_url) as string}
                            alt={image.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="p-3 space-y-2">
                          <p className="text-xs font-medium text-black whitespace-normal break-words">
                            {image.name}
                          </p>
                          <div className="flex flex-wrap gap-3 text-xs font-medium">
                            {image.live_url && (
                              <Link
                                target="_blank"
                                to={image.live_url}
                                className="text-blue-500 hover:underline"
                              >
                                View
                              </Link>
                            )}
                            {image.live_url && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownload(
                                    image.live_url as string,
                                    image.name,
                                  )
                                }
                                className="text-[#475467] hover:underline"
                              >
                                Download
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {!hasStructuredFeedback && revisionImages.length > 0 && (
        <div className="mt-4">
          <p className="text-[13px] mb-3 text-[#475467] font-medium">
            Reference Images
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {revisionImages.map((image, index) => (
              <div
                key={`${image.name}-${index}`}
                className="border border-[#EAECF0] rounded-[10px] overflow-hidden bg-white"
              >
                <div className="aspect-[4/3] bg-[#F7F7F7]">
                  <img
                    src={(image.live_url || image.local_url) as string}
                    alt={image.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-xs font-medium text-black whitespace-normal break-words">
                    {image.name}
                  </p>
                  <p className="text-xs text-[#959FB2]">
                    {image.size ? formatFileSize(image.size) : ""}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs font-medium">
                    {image.live_url && (
                      <Link
                        target="_blank"
                        to={image.live_url}
                        className="text-blue-500 hover:underline"
                      >
                        View
                      </Link>
                    )}
                    {image.live_url && (
                      <button
                        type="button"
                        onClick={() =>
                          handleDownload(image.live_url as string, image.name)
                        }
                        className="text-[#475467] hover:underline"
                      >
                        Download
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
